import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Shield, Check, Copy, ShieldCheck, Flame, Award, Heart, Globe } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_META = {
  platinum: { label: 'Platinum', color: 'from-slate-200 to-slate-400', text: 'text-slate-900', ring: 'ring-slate-300' },
  gold:     { label: 'Gold',     color: 'from-amber-300 to-amber-500', text: 'text-amber-950', ring: 'ring-amber-400' },
  trusted:  { label: 'Trusted',  color: 'from-emerald-400 to-emerald-600', text: 'text-emerald-950', ring: 'ring-emerald-400' },
  new:      { label: 'New',      color: 'from-zinc-500 to-zinc-700', text: 'text-zinc-50', ring: 'ring-zinc-500' },
};

const BADGE_LABELS = {
  clean_checkin: 'Clean check-ins',
  streak_3_clean: '3 in a row',
  streak_5_clean: '5 in a row',
  streak_10_clean: '10 in a row',
  age_verified_18plus: '18+ verified',
  age_verified_21plus: '21+ verified',
  age_verified_25plus: '25+ verified',
  parent: 'Travels with family',
};

const CATEGORY_LABELS = {
  dietary: { icon: '🥗', label: 'Dietary' },
  room: { icon: '🏙️', label: 'Room' },
  amenity: { icon: '💎', label: 'Amenities I value' },
  bed: { icon: '🛏️', label: 'Bed' },
};

export default function TrustedGuestProfilePage() {
  const { handle } = useParams();
  const [data, setData] = useState(null);
  const [venueStats, setVenueStats] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/trusted-guest/public/${handle}`);
        setData(r.data);
        // Non-blocking: fetch venue-partner attribution stats
        axios.get(`${BACKEND_URL}/api/venue-partners/stats/${handle}`)
          .then((s) => setVenueStats(s.data))
          .catch(() => {});
      } catch (e) {
        setError(e?.response?.status === 404 ? 'This trusted badge is private or does not exist.' : 'Something went wrong.');
      } finally {
        setLoading(false);
      }
    })();
  }, [handle]);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Share link copied');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] text-zinc-100 flex items-center justify-center">
        <div className="animate-pulse text-zinc-400">Loading trusted badge…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] text-zinc-100 flex items-center justify-center p-6">
        <Card className="bg-zinc-900 border-zinc-800 max-w-md w-full" data-testid="trusted-badge-error">
          <CardContent className="p-8 text-center space-y-3">
            <Shield className="w-12 h-12 text-zinc-500 mx-auto" />
            <p className="text-zinc-400">{error || 'Badge not found'}</p>
            <Link to="/" className="text-amber-400 text-sm hover:underline">Back to getbitsy.ai</Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const tier = TIER_META[data.trusted_tier] || TIER_META.new;

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-zinc-100" data-testid="trusted-guest-profile">
      {/* top bar */}
      <header className="border-b border-zinc-800/60 px-6 py-4 flex justify-between items-center">
        <Link to="/" className="text-sm tracking-widest text-zinc-400 hover:text-zinc-200">
          BHERMES · Trusted Guest
        </Link>
        <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px] tracking-widest">
          ZERO PII
        </Badge>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16 grid md:grid-cols-5 gap-10">
        {/* Badge card */}
        <div className="md:col-span-3">
          <Card className={`bg-gradient-to-br ${tier.color} border-0 ring-4 ${tier.ring} overflow-hidden`}>
            <CardContent className="p-10 relative">
              <div className="absolute top-6 right-6">
                <Badge className="bg-black/20 text-white border-0 text-[10px] tracking-widest">{tier.label.toUpperCase()}</Badge>
              </div>

              <div className="flex items-center gap-4 mb-8">
                <div className="w-16 h-16 rounded-2xl bg-black/25 flex items-center justify-center">
                  <ShieldCheck className={`w-9 h-9 ${tier.text}`} />
                </div>
                <div>
                  <p className={`text-xs tracking-widest opacity-70 ${tier.text}`}>TRUSTED GUEST</p>
                  <p className={`text-2xl font-semibold ${tier.text}`}>{data.handle}</p>
                </div>
              </div>

              <div className={`space-y-4 ${tier.text}`}>
                <div className="flex items-center gap-3">
                  {data.age_verified ? (
                    <>
                      <Check className="w-5 h-5" />
                      <span className="text-lg">Age verified <b>{data.age_bucket}+</b></span>
                    </>
                  ) : (
                    <span className="opacity-70 text-sm">Age not verified</span>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-black/10">
                  <Stat label="Score" value={data.score} tier={tier} />
                  <Stat label="Stays" value={data.total_stays} tier={tier} />
                  <Stat label="Streak" value={`${data.current_streak}/${data.longest_streak}`} tier={tier} />
                </div>

                {data.badges?.length > 0 && (
                  <div className="pt-6 flex flex-wrap gap-2">
                    {data.badges.map((b) => (
                      <span key={b}
                        className="px-3 py-1 rounded-full text-xs bg-black/20 border border-black/10"
                        data-testid={`trusted-badge-${b}`}>
                        {BADGE_LABELS[b] || b}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Portable BHERMES Passport preferences — already in the trusted payload */}
          {data.passport_preferences && (Object.keys(data.passport_preferences.by_category || {}).length > 0 || data.passport_preferences.language) && (
            <Card className="bg-zinc-900 border-zinc-800 mt-4" data-testid="passport-preferences-card">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl bg-amber-500/15 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-zinc-100">BHERMES Passport</p>
                    <p className="text-[11px] text-zinc-500">Portable preferences across every hotel in the network</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {Object.entries(data.passport_preferences.by_category || {}).map(([cat, tags]) => (
                    <div key={cat} className="flex flex-wrap items-center gap-2" data-testid={`passport-category-${cat}`}>
                      <span className="text-[11px] tracking-wide text-zinc-500 w-20 shrink-0">
                        {CATEGORY_LABELS[cat]?.icon} {CATEGORY_LABELS[cat]?.label || cat}
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {tags.map((tag) => (
                          <span
                            key={`${cat}-${tag}`}
                            className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-200 border border-amber-500/25"
                            data-testid={`passport-pref-${cat}-${tag}`}
                          >
                            {tag.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  {data.passport_preferences.language && (
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] tracking-wide text-zinc-500 w-20 shrink-0">🗣️ Language</span>
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-200 border border-amber-500/25" data-testid="passport-pref-language">
                        {data.passport_preferences.language}
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-zinc-500 pt-1 border-t border-zinc-800/60">
                  Set by the holder — visible so hotels know how to host them from the first interaction.
                </p>
              </CardContent>
            </Card>
          )}

          <p className="text-xs text-zinc-500 mt-4">
            This badge is a soulbound reputation signal from the BHERMES Protocol. It contains <b>no personal data</b> —
            no name, no email, no wallet, no date of birth. It proves the holder has a verified age and a clean
            hospitality record across getbitsy hotels.
          </p>

          {venueStats && venueStats.trusted_by_venues > 0 && (
            <Card className="bg-zinc-900 border-zinc-800 mt-4" data-testid="trusted-by-venues-card">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-rose-500/15 flex items-center justify-center shrink-0">
                  <Heart className="w-6 h-6 text-rose-400 fill-rose-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xl font-semibold text-zinc-100" data-testid="trusted-by-count">
                    Trusted by {venueStats.trusted_by_venues} venue{venueStats.trusted_by_venues === 1 ? '' : 's'}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {Object.entries(venueStats.by_type || {}).map(([t, n]) => (
                      <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700">
                        {n}× {t}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Share side */}
        <aside className="md:col-span-2 space-y-4">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6 space-y-4">
              <p className="text-xs tracking-widest text-zinc-400">SHARE THIS BADGE</p>
              <div className="bg-white p-4 rounded-lg flex justify-center" data-testid="trusted-qr">
                <QRCodeCanvas value={shareUrl} size={180} />
              </div>
              <Button
                variant="outline"
                className="w-full border-zinc-700 text-zinc-200 hover:bg-zinc-800"
                onClick={copyLink}
                data-testid="trusted-copy-link">
                <Copy className="w-4 h-4 mr-2" /> Copy link
              </Button>
              <p className="text-[11px] text-zinc-500 text-center leading-relaxed">
                Show this at bars, events, or venues to prove your age and trusted-guest status without handing over an ID.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6 space-y-2">
              <p className="text-xs tracking-widest text-zinc-400 flex items-center gap-2">
                <Award className="w-3 h-3" /> WHY IT WORKS
              </p>
              <ul className="text-sm text-zinc-300 space-y-2">
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> Verified on BHERMES protocol</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> Soulbound — can't be sold or faked</li>
                <li className="flex gap-2"><Flame className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" /> Score updates with every clean stay</li>
              </ul>
            </CardContent>
          </Card>
        </aside>
      </main>

      <footer className="border-t border-zinc-800/60 text-center py-6 text-[11px] text-zinc-600 tracking-wider">
        Powered by BHERMES · M.O.A.T. · <Link to="/" className="hover:text-zinc-400">getbitsy.ai</Link>
        <span className="mx-2">·</span>
        <Link to="/verify-badge" className="hover:text-amber-400" data-testid="door-staff-link">For door staff: verify a badge</Link>
      </footer>
    </div>
  );
}

function Stat({ label, value, tier }) {
  return (
    <div>
      <p className={`text-[10px] tracking-widest opacity-60 ${tier.text}`}>{label.toUpperCase()}</p>
      <p className={`text-xl font-semibold ${tier.text}`}>{value}</p>
    </div>
  );
}
